// ─────────────────────────────────────────────────────────────────────────────
// Customer Emails Reducer
// ─────────────────────────────────────────────────────────────────────────────
// State interface, initial state, and reducer for the customerEmails slice.
// State is partitioned by customer_id so the cache survives navigating between
// customer profiles within the same SPA session.
// ─────────────────────────────────────────────────────────────────────────────

import { Action, createReducer, on } from '@ngrx/store';
import { ICompanyEmail, IStructuredApiError } from '../models/customer-email.models';
import * as customerEmailsActions from './customer-emails.actions';

// ── State interface ──────────────────────────────────────────────────────────

export interface CustomerEmailsBucket {
  erp: ICompanyEmail[];
  volta: ICompanyEmail[];
}

export interface CustomerEmailsState {
  byCustomerId: Record<number, CustomerEmailsBucket>;
  loadingCustomerId: number | null;
  error: string | null;
  lastMutationError: IStructuredApiError | null;
}

// ── Initial state ────────────────────────────────────────────────────────────

export const initialCustomerEmailsState: CustomerEmailsState = {
  byCustomerId: {},
  loadingCustomerId: null,
  error: null,
  lastMutationError: null,
};

// ── Helpers ──────────────────────────────────────────────────────────────────

const emptyBucket = (): CustomerEmailsBucket => ({ erp: [], volta: [] });

const partition = (rows: ICompanyEmail[]): CustomerEmailsBucket => {
  const erp: ICompanyEmail[] = [];
  const volta: ICompanyEmail[] = [];
  for (const r of rows) {
    if (r.source === 'erp') erp.push(r);
    else volta.push(r);
  }
  return { erp, volta };
};

// ── Reducer ──────────────────────────────────────────────────────────────────

const featureReducer = createReducer(
  initialCustomerEmailsState,

  on(customerEmailsActions.LoadCustomerEmails, (state, { customerId }) => ({
    ...state,
    loadingCustomerId: customerId,
    error: null,
    lastMutationError: null,
  })),

  on(customerEmailsActions.LoadCustomerEmailsSuccess, (state, { customerId, response }) => ({
    ...state,
    loadingCustomerId: null,
    byCustomerId: {
      ...state.byCustomerId,
      [customerId]: partition(response.data || []),
    },
  })),

  on(customerEmailsActions.LoadCustomerEmailsError, (state, { error }) => ({
    ...state,
    loadingCustomerId: null,
    error,
  })),

  on(customerEmailsActions.CreateCustomerEmailSuccess, (state, { email }) => {
    const existing = state.byCustomerId[email.customer_id] ?? emptyBucket();
    return {
      ...state,
      lastMutationError: null,
      byCustomerId: {
        ...state.byCustomerId,
        [email.customer_id]: {
          erp: existing.erp,
          volta: [email, ...existing.volta],
        },
      },
    };
  }),

  on(customerEmailsActions.CreateCustomerEmailError, (state, { error }) => ({
    ...state,
    lastMutationError: error,
  })),

  on(customerEmailsActions.UpdateCustomerEmailSuccess, (state, { email }) => {
    const existing = state.byCustomerId[email.customer_id] ?? emptyBucket();
    return {
      ...state,
      lastMutationError: null,
      byCustomerId: {
        ...state.byCustomerId,
        [email.customer_id]: {
          erp: existing.erp,
          volta: existing.volta.map((row) => (row.id === email.id ? email : row)),
        },
      },
    };
  }),

  on(customerEmailsActions.UpdateCustomerEmailError, (state, { error }) => ({
    ...state,
    lastMutationError: error,
  })),

  on(customerEmailsActions.DeleteCustomerEmailSuccess, (state, { id, customerId }) => {
    const existing = state.byCustomerId[customerId];
    if (!existing) return { ...state, lastMutationError: null };
    return {
      ...state,
      lastMutationError: null,
      byCustomerId: {
        ...state.byCustomerId,
        [customerId]: {
          erp: existing.erp,
          volta: existing.volta.filter((row) => row.id !== id),
        },
      },
    };
  }),

  on(customerEmailsActions.DeleteCustomerEmailError, (state, { error }) => ({
    ...state,
    lastMutationError: error,
  })),

  on(customerEmailsActions.ClearCustomerEmailsError, (state) => ({
    ...state,
    lastMutationError: null,
    error: null,
  })),
);

export function customerEmailsReducer(state: CustomerEmailsState | undefined, action: Action) {
  return featureReducer(state, action);
}
